import { AsyncPipe, CurrencyPipe, NgClass } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { DjangoService, Routes } from '@core/services/django.service';
import { EnvironmentService } from '@core/services/environment.service';
import { EventTrackerService } from '@core/services/event-tracker.service';
import { WINDOW } from '@core/services/window.service';
import { ANALYTICS_EVENTS } from '@core/utils/AnalyticsEvents';
import { ModalService } from '@features/modal/services/modal.service';
import { TripsItinerarySectionDirective } from '@features/trips/directives/trips-itinerary-section.directive';
import { TripsItineraryMenuService } from '@features/trips/services/trips-itinerary-menu.service';
import { Itinerary } from '@generated/graphql';
import { LeadRequestCreatePolymorphicTypes as LeadRequestType } from '@generated/openapi/model/leadRequestCreatePolymorphicTypes';
import { PublicDeparture } from '@generated/openapi/model/publicDeparture';
import { EnquireFormModalComponent } from '@shared/enquire-form-modal/enquire-form-modal.component';
import {
    BehaviorSubject,
    Observable,
    Subject,
    fromEvent,
    takeUntil,
} from 'rxjs';
import { shareReplay } from 'rxjs/operators';
@Component({
    selector: 'app-trips-itinerary-navigation',
    templateUrl: './trips-itinerary-navigation.component.html',
    imports: [NgClass, AsyncPipe, CurrencyPipe],
})
export class TripsItineraryNavigationComponent
    implements OnInit, OnDestroy, OnChanges
{
    @Input() itinerary: Itinerary;
    @Input() selfBookingDeparturesContainer: ElementRef<HTMLDivElement>;
    menuOpen: boolean = false;
    menuItems: Observable<TripsItinerarySectionDirective[]>;
    activeMenuItem: Observable<TripsItinerarySectionDirective>;
    departures: Observable<PublicDeparture[]>;
    private destroy = new Subject<any>();

    observerOptions: IntersectionObserverInit = {
        root: null,
        // remove 100px from top of the viewport container
        rootMargin: '-100px 0px 0px 0px',
        threshold: 0.0,
    };
    isVisible = new BehaviorSubject<boolean>(false);
    private observer: IntersectionObserver;

    constructor(
        private eventTrackerService: EventTrackerService,
        private modalService: ModalService,
        private djangoService: DjangoService,
        private cd: ChangeDetectorRef,
        private tripsItineraryMenuService: TripsItineraryMenuService,
        private environmentService: EnvironmentService,
        @Inject(WINDOW) private window: Window
    ) {
        if (this.environmentService.isBrowser()) {
            const options = this.observerOptions;
            this.observer = new IntersectionObserver((entries, observer) => {
                const isVisible = entries
                    .map(value => value.isIntersecting)
                    .reduce(
                        (previousValue, currentValue) =>
                            previousValue && currentValue,
                        true
                    );
                this.isVisible.next(isVisible);
            }, options);
        }
    }

    ngOnDestroy(): void {
        this.destroy.next(null);
        if (this.environmentService.isBrowser()) {
            this.observer.disconnect();
        }
    }
    ngOnChanges(changes: SimpleChanges): void {
        if (
            this.environmentService.isBrowser() &&
            changes.selfBookingDeparturesContainer
        ) {
            const containerElement =
                changes.selfBookingDeparturesContainer?.currentValue
                    ?.nativeElement;
            if (this.observer && containerElement) {
                this.observer.observe(containerElement);
            }
        }
    }
    ngOnInit(): void {
        this.menuItems = this.tripsItineraryMenuService.sections;
        this.activeMenuItem = this.tripsItineraryMenuService.activeMenuItem;

        this.departures = this.djangoService
            .drfList<PublicDeparture>(Routes.publicDepartures, 'start_date', {
                itinerary: this.itinerary?.linkedVirtualOfficeItinerary,
            })
            .pipe(shareReplay(1));
        if (this.environmentService.isBrowser()) {
            fromEvent(this.window, 'click')
                .pipe(takeUntil(this.destroy))
                .subscribe(event => {
                    if (this.menuOpen) {
                        this.toggleMenu(event);
                    }
                });
        }
    }

    navigate(menuItem: TripsItinerarySectionDirective) {
        menuItem.scrollIntoView();
    }

    showDatesModal() {
        this.selfBookingDeparturesContainer.nativeElement.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
        this.eventTrackerService.trackEvent(
            ANALYTICS_EVENTS.ITINERARY_GROUP_DEPARTURES_CTA,
            {
                trip: this.itinerary.trip.title,
                itinerary: this.itinerary.title,
            }
        );
    }
    showCustomModal() {
        this.modalService.show(
            EnquireFormModalComponent,
            {
                trip: this.itinerary.trip,
                typeOfEnquiry: LeadRequestType.CustomDepartureLeadRequest,
                itinerary: this.itinerary,
                analyticsEventSent:
                    ANALYTICS_EVENTS.ITINERARY_CUSTOM_ENQUIRY_SENT,
                analyticsEventCompleted:
                    ANALYTICS_EVENTS.ITINERARY_CUSTOM_ENQUIRY_COMPLETED,
                analyticsEventAbandoned:
                    ANALYTICS_EVENTS.ITINERARY_CUSTOM_ENQUIRY_ABANDONED,
                analyticsEventData: {
                    trip: this.itinerary.trip.title,
                    itinerary: this.itinerary.title,
                },
            },
            false
        );
        this.eventTrackerService.trackEvent(
            ANALYTICS_EVENTS.ITINERARY_CUSTOM_TRIP_CTA,
            {
                trip: this.itinerary.trip.title,
                itinerary: this.itinerary.title,
            }
        );
    }

    toggleMenu(event: Event, stopPropagation: boolean = false) {
        this.menuOpen = !this.menuOpen;
        if (stopPropagation) {
            event.stopPropagation();
        }
        this.cd.detectChanges();
    }
}
